import { Injectable, inject } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { DeviceMode, ESelectedEntityType } from '@common/types/layout.type';

import { WebWidgetsFactoryService } from '@web/core/services/web-widgets-factory.service';
import { WebWidgetsService } from '@web/core/services/web-widgets.service';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { NewZeroWidgetsService } from '@web/widgets/common/zero-size-sync/new-zero-widgets.service';

import { WidgetsFacade } from './widgets.facade';

import LayoutActions from '@web/store/layout/layout.actions';
import WidgetsLegacyActions, { WidgetsActions } from './widgets.actions';

@Injectable()
export class WidgetsEffects {
    private readonly actions$ = inject(Actions);
    private readonly widgetsFactoryService = inject(WebWidgetsFactoryService);
    private readonly widgetsService = inject(WebWidgetsService);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly layoutFacade = inject(LayoutFacade);
    private readonly newZeroWidgets = inject(NewZeroWidgetsService);

    private readonly markNewZeroWidget$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(WidgetsLegacyActions.widgetCreated),
                filter(({ widget }) => widget.type === 'zero'),
                tap(({ widget }) => this.newZeroWidgets.mark(widget.id))
            ),
        { dispatch: false }
    );

    private readonly createWidget$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.createWidget),
            withLatestFrom(
                this.widgetsFacade.widgets$,
                this.layoutFacade.deviceMode$,
                (props, widgets, deviceMode) => ({
                    ...props,
                    widgets,
                    deviceMode,
                })
            ),
            map(({ columnId, index, widget, skipHistory, meta, deviceMode }) => {
                const widgetModel = this.widgetsService.getWidgetModel(widget);

                if (widgetModel.createEntity) {
                    return WidgetsActions.created({
                        widget: this.widgetsFactoryService.createWidget(widgetModel, columnId),
                        index,
                        skipHistory,
                    });
                }

                const newWidgetInstance = this.widgetsFactoryService.createWidgetHandler(widgetModel, {
                    ...meta,
                    columnId,
                });

                if (newWidgetInstance.type === 'zero') {
                    const DEFAULT_Y_OFFSET = 50;

                    const constructorScrollWrapper = document.getElementById('constructorScrollWrapper');
                    const columnElement = document.getElementById(columnId);

                    let scrollTop = DEFAULT_Y_OFFSET;

                    if (constructorScrollWrapper && columnElement) {
                        const wrapperRect = constructorScrollWrapper.getBoundingClientRect();
                        const columnRect = columnElement.getBoundingClientRect();
                        const isInViewport = columnRect.top + DEFAULT_Y_OFFSET >= wrapperRect.top;

                        if (!isInViewport) {
                            const scrolledHeight = wrapperRect.top - columnRect.top;
                            scrollTop += scrolledHeight;
                        }
                    }

                    if (deviceMode === DeviceMode.mobile) {
                        newWidgetInstance.data.position.mobileY = scrollTop;
                        newWidgetInstance.data.position.tabletY = DEFAULT_Y_OFFSET;
                        newWidgetInstance.data.position.y = DEFAULT_Y_OFFSET;
                    } else if (deviceMode === DeviceMode.tablet) {
                        newWidgetInstance.data.position.tabletY = scrollTop;
                        newWidgetInstance.data.position.mobileY = DEFAULT_Y_OFFSET;
                        newWidgetInstance.data.position.y = DEFAULT_Y_OFFSET;
                    } else {
                        newWidgetInstance.data.position.y = scrollTop;
                        newWidgetInstance.data.position.mobileY = DEFAULT_Y_OFFSET;
                        newWidgetInstance.data.position.tabletY = DEFAULT_Y_OFFSET;
                    }
                }

                // console.log({ newWidgetInstance, columnId });
                return WidgetsLegacyActions.widgetCreated({
                    widget: { ...newWidgetInstance, columnId },
                    index,
                    skipHistory,
                });
            })
        )
    );

    private readonly copyZeroWidget$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.copyZeroWidget),
            switchMap(({ widget, index }) => {
                const newWidget = structuredClone(widget);

                if (newWidget.type === 'zero') {
                    newWidget.data.position.y += 30;
                    newWidget.data.position.mobileY += 30;
                    newWidget.data.position.tabletY += 30;

                    newWidget.data.position.x += 30;
                    newWidget.data.position.mobileX += 30;
                    newWidget.data.position.tabletX += 30;
                }

                return [
                    WidgetsLegacyActions.copyWidget({
                        widget: newWidget,
                        index,
                    }),
                    LayoutActions.setSelectedEntity({
                        selectedEntity: {
                            entityId: newWidget.id,
                            entityType: ESelectedEntityType.widget,
                        },
                    }),
                ];
            })
        )
    );

    private readonly createWidgetToGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.createWidgetToGrid),
            map(({ columnId, gridId, gridCellIndex, index, widget }) => {
                const widgetModel = this.widgetsService.getWidgetModel(widget);

                if (widgetModel.createEntity) {
                    return WidgetsActions.created({
                        widget: this.widgetsFactoryService.createWidget(widgetModel, gridId),
                        gridCellIndex,
                        index,
                    });
                }

                const newWidgetInstance = this.widgetsFactoryService.createWidgetHandler(widgetModel, {
                    columnId,
                });
                return WidgetsLegacyActions.widgetCreatedToGrid({
                    widget: { ...newWidgetInstance, columnId, structureId: gridId },
                    gridCellIndex,
                    index,
                });
            })
        )
    );
}
